import { useState } from 'react';
import { useMemo } from 'react';
import { displayError } from '../../helpers';
import { buildListExcelExportHeader } from '../../utils/listExcelExportHeader';
import { exportAsExcel } from '../../utils/ExcelExporter';
import { createPdfBlob } from '../../utils/PDFExporter';
import { openPdfInApp } from '../../utils/openPdfInApp';
import { formatQty } from './todayQtySummaryUtils';

const COLUMNS = [
  { key: 'dcNo', label: 'DC No' },
  { key: 'date', label: 'Date' },
  { key: 'time', label: 'Time' },
  { key: 'plant', label: 'Plant' },
  { key: 'customer', label: 'Customer' },
  { key: 'site', label: 'Site' },
  { key: 'grade', label: 'Grade' },
  { key: 'deliveryMode', label: 'Mode of Delivery' },
  { key: 'dcQty', label: 'DC Qty', align: 'right' },
];

function mapCancelledDc(dc) {
  const salesOrder = dc.work_schedule?.sales_order_detail?.sales_order;
  const dcNo = dc.prefix
    ? `${dc.prefix}/${dc.delivery_challan_no || ''}`
    : (dc.delivery_challan_no || '-');
  return {
    id: dc.id,
    dcNo,
    date: dc.delivery_challan_date || '-',
    time: dc.delivery_challan_time || '-',
    plant: dc.work_schedule?.plant?.alias_name
      || dc.production_plant?.alias_name
      || '-',
    customer: salesOrder?.consignee?.customer?.name || '-',
    site: salesOrder?.consignee?.project_name || '-',
    grade: dc.work_schedule?.sales_order_detail?.product?.name || '-',
    deliveryMode: (dc.work_schedule?.delivery_mode || '-').toString().toUpperCase(),
    dcQty: Number(dc.dc_qty) || 0,
  };
}

function CancelledDcDetailModal({ open, onClose, records, periodLabel, cookies, plantId }) {
  const rows = useMemo(
    () => (records || [])
      .map(mapCancelledDc)
      .sort((left, right) => (
        String(right.date).localeCompare(String(left.date))
        || String(left.dcNo).localeCompare(String(right.dcNo))
      )),
    [records],
  );
  const totalQty = useMemo(
    () => rows.reduce((sum, row) => sum + row.dcQty, 0),
    [rows],
  );
  const [exportingExcel, setExportingExcel] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const canExport = rows.length > 0;
  const fileStamp = String(periodLabel || 'cancelled').replace(/\s+/g, '_');

  const handleExcelExport = () => {
    setExportingExcel(true);
    try {
      const columns = COLUMNS.map((column) => column.label);
      const sheetRows = [
        ...rows.map((row) => COLUMNS.map((column) => (
          column.key === 'dcQty' ? formatQty(row.dcQty) : row[column.key]
        ))),
        ['Total', '', '', '', '', '', '', '', formatQty(totalQty)],
      ];
      exportAsExcel(columns, sheetRows, `cancelled_dc_${fileStamp}.xlsx`);
    } catch (error) {
      displayError(error?.response?.data ?? error, 'Excel export failed');
    } finally {
      setExportingExcel(false);
    }
  };

  const handlePdfExport = async () => {
    setExportingPdf(true);
    try {
      const plantIds = plantId ? [plantId] : [];
      const exportHeader = await buildListExcelExportHeader(cookies, plantIds, {
        documentTitle: 'Cancelled Delivery Challans',
        extraInfoLines: [`Period: ${periodLabel}`],
      });
      const pdfColumns = COLUMNS.map((column) => ({
        header: column.label,
        dataKey: column.key,
        align: column.align === 'right' ? 'right' : 'left',
      }));
      const pdfRows = [
        ...rows.map((row) => ({
          ...row,
          dcQty: formatQty(row.dcQty),
        })),
        {
          dcNo: 'Total',
          date: '',
          time: '',
          plant: '',
          customer: '',
          site: '',
          grade: '',
          deliveryMode: '',
          dcQty: formatQty(totalQty),
        },
      ];
      const blob = createPdfBlob(
        '',
        pdfColumns,
        pdfRows,
        exportHeader.infoLines?.length
          ? exportHeader.infoLines
          : [`Period: ${periodLabel}`],
        {
          companyName: exportHeader.companyName,
          addressLines: exportHeader.addressLines,
          gstNo: exportHeader.gstNo,
          logoDataUrl: exportHeader.logoDataUrl,
          documentTitle: 'Cancelled Delivery Challans',
          subtitle: periodLabel,
        },
      );
      openPdfInApp(blob, {
        title: `Cancelled Delivery Challans (${periodLabel})`,
        downloadFileName: `cancelled_dc_${fileStamp}.pdf`,
      });
    } catch (error) {
      displayError(error?.response?.data ?? error, 'PDF export failed');
    } finally {
      setExportingPdf(false);
    }
  };

  if (!open) {
    return null;
  }

  return (
    <div className="modal today-qty-summary-modal" tabIndex="-1" style={{ display: 'block' }}>
      <div className="modal-dialog modal-xl modal-dialog-scrollable">
        <div className="modal-content">
          <div className="modal-header today-qty-summary-modal-header">
            <h5 className="modal-title">Cancelled Delivery Challans ({periodLabel})</h5>
            <div className="today-qty-summary-header-actions">
              {canExport && (
                <>
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    title="View PDF"
                    aria-label="View PDF"
                    disabled={exportingPdf}
                    onClick={handlePdfExport}
                  >
                    <i className="fa fa-file-pdf-o text-danger" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-light"
                    title="Download Excel"
                    aria-label="Download Excel"
                    disabled={exportingExcel}
                    onClick={handleExcelExport}
                  >
                    <i className="fa fa-file-excel-o text-success" aria-hidden="true" />
                  </button>
                </>
              )}
              <button type="button" className="close text-white" aria-label="Close" onClick={onClose}>
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </div>
          <div className="modal-body">
            {rows.length === 0 ? (
              <div className="text-center py-4 text-muted">
                No cancelled delivery challans for {periodLabel}.
              </div>
            ) : (
              <div className="table-responsive no-column-freeze">
                <table className="table table-bordered today-qty-summary-table">
                  <thead>
                    <tr>
                      {COLUMNS.map((column) => (
                        <th
                          key={column.key}
                          className={column.align === 'right' ? 'text-right' : undefined}
                        >
                          {column.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => (
                      <tr key={row.id || `${row.dcNo}-${row.date}-${row.time}`}>
                        {COLUMNS.map((column) => (
                          <td
                            key={column.key}
                            className={column.align === 'right' ? 'text-right' : undefined}
                          >
                            {column.key === 'dcQty' ? formatQty(row.dcQty) : row[column.key]}
                          </td>
                        ))}
                      </tr>
                    ))}
                    <tr className="today-qty-summary-total-row">
                      <td>Total</td>
                      <td colSpan={7} />
                      <td className="text-right">{formatQty(totalQty)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CancelledDcDetailModal;
